import { NotificationType } from '@electricjs/core_entity-client';

import { Flex, Text } from '@electricjs/arc';
import { Trans } from 'react-i18next';

import i18n from '@/i18n';
import ApplicationIcon from '@/components/Applications/ApplicationIcon';
import { useGetRequestData } from '@/hooks/useGetRequestData';
import { Application } from '@/types/applications';
import { EmployeeDirectoryListing } from '@/types/employees';
import { RequestTypeSlugEnum } from '@/types/requests';

import { EmailDetail } from '../EmailDetail';
import { GroupDetail } from '../GroupDetail';
import { NotificationIcon } from '../NotificationIcon';
import { NotificationItemHeader } from '../NotificationItemHeader';
import { RequestActions } from '../RequestActions/RequestActions';
import { NotificationItemProps } from '../types';
import { getDisplayFullName } from 'common/utils/getFullName';

const getTitle = (
  employee?: EmployeeDirectoryListing,
  application?: Application,
  requestTypeSlug?: string,
  notificationType?: NotificationType
) => {
  // Title
  const employeeName = employee
    ? getDisplayFullName(employee)
    : i18n.t('common:notificationItems.anEmployee');
  const applicationName = application
    ? application.name
    : i18n.t('common:notificationItems.applicationRequest.anApplication');

  switch (requestTypeSlug) {
    case RequestTypeSlugEnum.CREATE_ACCOUNT:
      return (
        <Text variant="legal">
          {notificationType === NotificationType.RequestAutomationFailed && (
            <Text variant="legal">
              <Trans
                i18nKey="notificationItems.applicationRequest.automationFailed"
                ns="common"
                components={{ bold: <b /> }}
              />
              <br />
            </Text>
          )}
          <Trans
            i18nKey="notificationItems.applicationRequest.requiresAccess"
            ns="common"
            values={{ employeeName, applicationName }}
            components={{ bold: <b /> }}
          />
        </Text>
      );
    case RequestTypeSlugEnum.REMOVE_ACCOUNT:
      return (
        <Text variant="legal">
          <Trans
            i18nKey="notificationItems.applicationRequest.revokeAccess"
            ns="common"
            values={{ employeeName, applicationName }}
            components={{ bold: <b /> }}
          />
        </Text>
      );
    case RequestTypeSlugEnum.INTEGRATION_NEEDS_REAUTH:
      return (
        <Text variant="legal">
          <Trans
            i18nKey="notificationItems.applicationRequest.needsReauth"
            ns="common"
            values={{ applicationName }}
            components={{ bold: <b /> }}
          />
        </Text>
      );
    default:
      // This should never happen
      return (
        <Text variant="legal">
          <Trans
            i18nKey="notificationItems.applicationRequest.requiresAssistance"
            ns="common"
            values={{ employeeName, applicationName }}
            components={{ bold: <b /> }}
          />
        </Text>
      );
  }
};

export const ApplicationRequestActionsColumn = ({
  notification,
}: NotificationItemProps) => {
  const { data: requestData, isLoading } = useGetRequestData({
    requestId: notification?.data?.request_id ?? null,
    options: {
      includeRequestType: true,
    },
  });

  return (
    <RequestActions
      loading={isLoading}
      request={requestData.request}
      requestType={requestData.requestType}
      isDropDownMenu={false}
    />
  );
};

export const ApplicationRequestDetailsColumns = ({
  notification,
}: NotificationItemProps) => {
  const { data: requestData, isLoading } = useGetRequestData({
    requestId: notification?.data?.request_id ?? null,
    options: {
      includeEmployee: true,
      includeEmployeeGroups: true,
    },
  });

  const employeeGroups = requestData.employeeGroups;

  const employeeEmail = requestData?.employee?.email;

  return (
    <Flex flexDirection="column">
      <GroupDetail isLoading={isLoading} groups={employeeGroups} />
      <EmailDetail isLoading={isLoading} email={employeeEmail} />
    </Flex>
  );
};

const ApplicationRequestNotificationItem = ({
  notification,
  hideDetails,
}: NotificationItemProps) => {
  const { data: requestData, isLoading } = useGetRequestData({
    requestId: notification?.data?.request_id ?? null,
    options: {
      includeRequestType: true,
      includeEmployee: true,
      includeEmployeeGroups: true,
      includeApplication: true,
    },
  });

  const employee = requestData?.employee;

  const employeeGroups = requestData?.employeeGroups;

  const employeeEmail = employee?.email;

  const application = requestData?.application;

  const getLabel = () => {
    switch (notification.type) {
      case NotificationType.RequestAssigned:
        return i18n.t('common:notificationItems.labels.requestAssigned');

      case NotificationType.RequestCompleted:
        return i18n.t('common:notificationItems.labels.requestCompleted');

      case NotificationType.RequestAutomationFailed:
        return i18n.t('common:notificationItems.labels.requestAssigned');
      default:
        return '';
    }
  };

  const applicationIcon = () =>
    application ? (
      <ApplicationIcon
        name={application.name}
        icon={application.icon}
        size="2.6rem"
      />
    ) : (
      'app'
    );

  const getIcon = () => (
    <NotificationIcon
      isNotificationUnseen={!notification.seenAt}
      loading={isLoading}
      icon={applicationIcon()}
    />
  );

  return (
    <NotificationItemHeader
      notification={notification}
      isLoading={isLoading}
      title={getTitle(
        employee,
        requestData?.application,
        requestData?.requestType?.slug,
        notification.type
      )}
      icon={getIcon()}
      label={getLabel()}>
      {!hideDetails ? (
        <>
          <GroupDetail isLoading={isLoading} groups={employeeGroups} />
          <EmailDetail isLoading={isLoading} email={employeeEmail} />
          <RequestActions
            loading={isLoading}
            request={requestData.request}
            requestType={requestData.requestType}
          />
        </>
      ) : null}
    </NotificationItemHeader>
  );
};

export default ApplicationRequestNotificationItem;
